/**
 * supabase.ts — el proveedor de identidad, y NADA más.
 *
 * ⚠️ Acá no se consulta un solo dato de contabilidad.
 *
 * Supabase se usa para UNA cosa: autenticar y mantener la sesión del
 * navegador. Todo lo demás pasa por nuestra API, y no es una preferencia de
 * arquitectura — es lo que hace que el aislamiento exista.
 *
 * Un `select` desde el navegador contra las tablas del inquilino se saltaría
 * `control.abrir_sesion`, que es el único lugar que comprueba la membresía
 * antes de fijar `app.empresa_id`. Sin ese contexto la RLS no devuelve nada
 * —falla cerrado, que es correcto— pero el camino no debería ni existir.
 *
 * Por eso, y conviene que quede escrito donde se vea:
 *
 *   **Los esquemas `control` y `t_*` NO se agregan nunca a los «exposed
 *   schemas» del proyecto de Supabase.** Solo se llega a ellos por la API,
 *   con el rol `app_api` y dentro de una transacción con sesión abierta.
 *
 * La sesión la guarda el propio cliente de Supabase. Nosotros no tocamos el
 * token ni lo copiamos a ningún lado: un segundo lugar donde vive una
 * credencial es un segundo lugar del que se puede filtrar.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { configuracionValida } from './flujo.ts';

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const CLAVE = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * `false` mientras falten las variables O sigan siendo los marcadores del
 * ejemplo. Ver `configuracionValida`: comprobar solo que no estuvieran vacías
 * dejaba pasar `https://....supabase.co`, y entonces la pantalla de acceso
 * aparecía y fallaba como si la contraseña estuviera mal.
 */
export const configurado = configuracionValida(URL, CLAVE);

/**
 * `null` si no está configurado.
 *
 * Se devuelve nulo en vez de lanzar al importar: un módulo que revienta al
 * cargarse deja la aplicación en blanco, y una pantalla en blanco no dice qué
 * falta. Con `null`, el flujo llega a «sin configurar» y lo explica.
 */
export const supabase: SupabaseClient | null = configurado
  ? createClient(URL!, CLAVE!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // El token no viaja en la URL. Con `detectSessionInUrl` activo, un
        // enlace de acceso deja la credencial en el historial del navegador y
        // en el `Referer` de la primera petición que salga.
        detectSessionInUrl: false,
      },
    })
  : null;
